import React, { useState } from "react";
import { Sparkles, Send, CheckCircle2, User, Phone, Mail, Calendar, Car as CarIcon, MessageSquare, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { useSettings } from "../contexts/SettingsContext";

// IMPORTANT: defined OUTSIDE the component so it keeps a stable identity
// between renders. Defining it inside causes React to remount the children
// (the <input>) on every keystroke, which loses focus after each character.
function Field({ icon: Icon, label, required, children, testid }) {
  return (
    <div data-testid={testid ? `${testid}-wrap` : undefined}>
      <label
        className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] mb-1.5"
        style={{ color: "var(--brand-text-secondary)" }}
      >
        {Icon && <Icon className="w-3 h-3" style={{ color: "var(--brand-primary)" }} />}
        <span>{label}{required ? " *" : ""}</span>
      </label>
      {children}
    </div>
  );
}

export default function QuoteForm() {
  const { t } = useI18n();
  const { settings } = useSettings();
  const [data, setData] = useState({
    name: "",
    phone: "",
    email: "",
    pickup_date: "",
    return_date: "",
    car_preference: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Honor admin toggle: if explicitly disabled, hide the whole section
  if (settings?.quote_form_enabled === false) return null;

  const update = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!data.name.trim() || !data.phone.trim()) {
      setError(t("home.quoteError"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...data, source: "home_form" });
      setSent(true);
    } catch (err) {
      setError(t("home.quoteError"));
    } finally {
      setLoading(false);
    }
  };

  // Polished input field with icon + floating label feel
  // (Field component is defined outside this function — see top of file)

  return (
    <section
      id="quote"
      className="py-20 md:py-28 px-4 md:px-6 max-w-[1200px] mx-auto"
      data-testid="quote-form-section"
    >
      <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-16 items-start">
        {/* Left: heading + benefits */}
        <div className="lg:sticky lg:top-24">
          <div className="label-eyebrow mb-4 inline-flex items-center" style={{ color: "var(--brand-primary)" }}>
            <Sparkles className="inline w-3.5 h-3.5 mr-1.5" />
            {t("home.quoteEyebrow")}
          </div>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-6 leading-[1.1]">
            {t("home.quoteTitle")}
          </h2>
          <p className="text-base leading-relaxed mb-8" style={{ color: "var(--brand-text-secondary)" }}>
            {t("home.quoteSubtitle")}
          </p>
          <ul className="space-y-3 text-sm">
            {[
              "Raspuns in maxim 30 minute",
              "Fara obligatii, oferta gratuita",
              "Pret transparent, fara taxe ascunse",
            ].map((line) => (
              <li key={line} className="flex items-start gap-3" style={{ color: "var(--brand-text-secondary)" }}>
                <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5" style={{ background: "rgba(212, 175, 55, 0.15)" }}>
                  <CheckCircle2 className="w-3.5 h-3.5" style={{ color: "var(--brand-primary)" }} />
                </div>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: form */}
        {sent ? (
          <div className="card-surface p-10 sm:p-12 text-center" data-testid="quote-form-success">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ background: "rgba(212, 175, 55, 0.15)" }}>
              <CheckCircle2 className="w-10 h-10" style={{ color: "var(--brand-primary)" }} />
            </div>
            <h3 className="font-serif text-2xl mb-3">{t("home.quoteSuccess")}</h3>
            <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>
              Te vom contacta in cel mai scurt timp cu o oferta personalizata.
            </p>
            <button
              type="button"
              onClick={() => { setSent(false); setData({ name: "", phone: "", email: "", pickup_date: "", return_date: "", car_preference: "", message: "" }); }}
              className="btn-ghost mt-6 text-xs"
            >
              Trimite alta cerere
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="card-surface p-6 sm:p-8 space-y-5"
            data-testid="quote-form"
            style={{ borderTop: "2px solid var(--brand-primary)" }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={User} label={t("home.quoteName")} required testid="quote-name">
                <input
                  type="text"
                  required
                  value={data.name}
                  onChange={update("name")}
                  className="form-input w-full"
                  placeholder="Ion Popescu"
                  data-testid="quote-name"
                />
              </Field>
              <Field icon={Phone} label={t("home.quotePhone")} required testid="quote-phone">
                <input
                  type="tel"
                  required
                  value={data.phone}
                  onChange={update("phone")}
                  className="form-input w-full"
                  placeholder="+40 7XX XXX XXX"
                  data-testid="quote-phone"
                />
              </Field>
            </div>

            <Field icon={Mail} label={t("home.quoteEmail")} testid="quote-email">
              <input
                type="email"
                value={data.email}
                onChange={update("email")}
                className="form-input w-full"
                placeholder="ion@example.com"
                data-testid="quote-email"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field icon={Calendar} label={t("home.quotePickup")} testid="quote-pickup">
                <input
                  type="date"
                  value={data.pickup_date}
                  onChange={update("pickup_date")}
                  className="form-input w-full"
                  data-testid="quote-pickup"
                />
              </Field>
              <Field icon={Calendar} label={t("home.quoteReturn")} testid="quote-return">
                <input
                  type="date"
                  value={data.return_date}
                  onChange={update("return_date")}
                  className="form-input w-full"
                  data-testid="quote-return"
                />
              </Field>
            </div>

            <Field icon={CarIcon} label={t("home.quoteCarPref")} testid="quote-car-pref">
              <input
                type="text"
                value={data.car_preference}
                onChange={update("car_preference")}
                className="form-input w-full"
                placeholder="Mercedes E-Class, BMW Seria 5..."
                data-testid="quote-car-pref"
              />
            </Field>

            <Field icon={MessageSquare} label={t("home.quoteMessage")} testid="quote-message">
              <textarea
                value={data.message}
                onChange={update("message")}
                rows={3}
                className="form-input w-full resize-none"
                placeholder="Detalii suplimentare, conditii speciale..."
                data-testid="quote-message"
              />
            </Field>

            {error && (
              <div className="text-sm flex items-center gap-2 px-3 py-2 rounded" style={{ background: "rgba(220, 38, 38, 0.1)", color: "#fca5a5" }} data-testid="quote-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-gold-filled w-full inline-flex items-center justify-center gap-2 py-3 text-sm uppercase tracking-[0.16em] disabled:opacity-60"
              data-testid="quote-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? t("home.quoteSubmitting") : t("home.quoteSubmit")}
            </button>

            <p className="text-[11px] text-center" style={{ color: "var(--brand-text-secondary)" }}>
              Datele tale sunt protejate si nu vor fi partajate cu terti.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
